import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Lock, PenLine } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, Spinner, UserChip, Time, RoleBadge } from "@/forum/ui";

const Icon = ({ name, ...p }) => {
  const C = Icons[name] || Icons.MessageSquare;
  return <C {...p} />;
};

const CategoryRow = ({ c, i }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04, duration: 0.5 }}>
    <Link
      to={`/forum/c/${c.slug}`}
      data-testid={`category-${c.slug}`}
      className="group grid grid-cols-[auto_1fr] items-center gap-4 px-5 py-5 transition-colors hover:bg-white/[0.03] sm:grid-cols-[auto_1fr_auto_auto] sm:gap-6 sm:px-6"
    >
      <span className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/[0.03] text-white/80 transition-colors group-hover:border-[rgba(94,177,255,0.5)] group-hover:text-[var(--glow)]">
        <Icon name={c.icon} size={18} />
      </span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-display text-base font-bold text-white sm:text-lg">{c.name}</h3>
          {c.min_read_role !== "player" && <Lock size={12} className="text-white/40" />}
          {c.min_write_role !== "player" && <span className="font-mono text-[9px] uppercase tracking-widest text-white/35">írás: {c.min_write_role}</span>}
        </div>
        <p className="mt-1 truncate text-sm text-white/50">{c.description}</p>
      </div>
      <div className="hidden text-right font-mono text-[11px] text-white/40 sm:block">
        <p><span className="text-white">{c.thread_count}</span> téma</p>
        <p><span className="text-white">{c.post_count}</span> hozzászólás</p>
      </div>
      <div className="hidden w-56 sm:block">
        {c.last_thread ? (
          <div className="text-right">
            <p className="truncate text-sm text-white/80">{c.last_thread.title}</p>
            <p className="mt-1 font-mono text-[11px] text-white/40">{c.last_thread.by?.username} · <Time iso={c.last_thread.at} /></p>
          </div>
        ) : <p className="text-right font-mono text-[11px] text-white/30">még nincs téma</p>}
      </div>
    </Link>
  </motion.div>
);

export default function ForumHome() {
  const [cats, setCats] = useState(null);
  const [stats, setStats] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user === null) return;
    api.get("/forum/categories").then((r) => setCats(r.data));
    api.get("/forum/stats").then((r) => setStats(r.data));
  }, [user]);

  if (!cats) return <Spinner />;
  const groups = [...new Set(cats.map((c) => c.group))];

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_300px]" data-testid="forum-home">
      <div className="space-y-8">
        <div>
          <p className="eyebrow mb-3">Bone County Roleplay</p>
          <h1 className="font-display text-3xl font-bold sm:text-4xl">Fórum</h1>
        </div>
        {groups.map((g) => (
          <Panel key={g} className="overflow-hidden" data-testid={`group-${g}`}>
            <div className="border-b border-white/[0.06] px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">{g}</div>
            <div className="divide-y divide-white/[0.05]">
              {cats.filter((c) => c.group === g).map((c, i) => <CategoryRow key={c.id} c={c} i={i} />)}
            </div>
          </Panel>
        ))}
      </div>
      <aside className="space-y-4">
        {user === false && (
          <Panel className="p-6" data-testid="forum-join-panel">
            <PenLine size={18} className="text-[var(--glow)]" />
            <h3 className="mt-3 font-display text-lg font-bold">Csatlakozz a beszélgetéshez</h3>
            <p className="mt-2 text-sm text-white/50">Regisztrálj, hogy témát nyithass és hozzászólhass.</p>
            <Link to="/register" className="btn-glow mt-5 inline-flex rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black" data-testid="forum-join-register">Regisztráció</Link>
          </Panel>
        )}
        {stats && (
          <Panel className="p-6" data-testid="forum-stats">
            <p className="eyebrow mb-4">Statisztika</p>
            <dl className="grid grid-cols-3 gap-3 text-center">
              {[["Tagok", stats.users], ["Témák", stats.threads], ["Hozzászólás", stats.posts]].map(([k, v]) => (
                <div key={k} className="rounded-2xl bg-white/[0.03] p-3">
                  <dt className="font-mono text-[10px] uppercase tracking-wider text-white/40">{k}</dt>
                  <dd className="mt-1 font-display text-xl font-bold">{v}</dd>
                </div>
              ))}
            </dl>
            {stats.latest_user && (
              <div className="mt-4 text-xs text-white/50">Legújabb tag: <UserChip user={stats.latest_user} /></div>
            )}
          </Panel>
        )}
        {stats && (
          <Panel className="p-6" data-testid="forum-roles">
            <p className="eyebrow mb-4">Rangok</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.roles).map(([r, l]) => <RoleBadge key={r} role={r} label={l} />)}
            </div>
          </Panel>
        )}
      </aside>
    </div>
  );
}
